import { useState } from "react";

import { TranslationKey } from "@impero-com/common/types/i18n";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { emitNotification, TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import {
    postControlResultsReassign,
    getGetControlResultsInfiniteQueryKey,
} from "api/generated/control-results/control-results";
import { StepIndex, NewAssignee, ReassignIds } from "api/generated/model";
import {
    UserOrUserGroupOverview,
    mapUserOrUserGroupOverview,
    userOrUserGroupToPartialUpdateTarget,
} from "utils/user";

import { ControlActivityTableRow } from "./types";

export interface ReassignSelection {
    responsible?: UserOrUserGroupOverview;
    firstReviewer?: UserOrUserGroupOverview;
    secondReviewer?: UserOrUserGroupOverview;
}

type ReassignRole = keyof ReassignSelection;

interface ReassignRoleConfig {
    /** The `ReassignSelection` field this role reads its picked assignee from. */
    key: ReassignRole;
    /** The `StepIndex` map key sent in the reassign payload to satisfy the BE enum. */
    stepIndex: StepIndex;
    labelKey: TranslationKey;
}

const REASSIGN_ROLES = [
    {
        key: "responsible",
        stepIndex: "Step0",
        labelKey: "ControlResult_ListReassignedAsResponsible",
    },
    {
        key: "firstReviewer",
        stepIndex: "Step1",
        labelKey: "ControlResult_ListReassignedAsReviewer",
    },
    {
        key: "secondReviewer",
        stepIndex: "Step2",
        labelKey: "ControlResult_ListReassignedAsReviewer",
    },
] as const satisfies readonly ReassignRoleConfig[];

function toNewAssignee(assignee: UserOrUserGroupOverview): NewAssignee {
    const { kind, data } = userOrUserGroupToPartialUpdateTarget(assignee);
    return kind === "User"
        ? { type: "User", value: data }
        : { type: "UserPool", value: data };
}

function assigneeName(assignee: UserOrUserGroupOverview): string {
    return mapUserOrUserGroupOverview(
        assignee,
        (user) => formatUserName(user),
        (group) => group.name
    );
}

/**
 * The user id a picked assignee contributes to the segregation-of-duties check:
 * `undefined` when the role isn't being reassigned (keep the current assignee),
 * `null` when reassigned to a pool (no individual user), else the user id.
 */
function pickedUserId(
    assignee: UserOrUserGroupOverview | undefined
): number | null | undefined {
    if (!assignee) {
        return undefined;
    }
    return mapUserOrUserGroupOverview(
        assignee,
        (user) => user.id,
        () => null
    );
}

/**
 * When SoD is enforced, the same individual user can't end up in more than one role after the
 * reassignment. Pools are exempt (see sc-39246). The backend
 * enforces this too; this is the client-side check for a notification.
 */
function wouldBreakSegregationOfDuties(
    row: ControlActivityTableRow,
    selection: ReassignSelection
): boolean {
    if (!row.segregationOfDutiesEnabled) {
        return false;
    }
    const picksByStep = [
        selection.responsible,
        selection.firstReviewer,
        selection.secondReviewer,
    ];
    const resultingUserIds = row.stepAssigneeUserIds.map(
        (currentUserId, stepIndex) => {
            const picked = pickedUserId(picksByStep[stepIndex]);
            return picked === undefined ? currentUserId : picked;
        }
    );
    const assignedUserIds = resultingUserIds.filter(
        (id): id is number => id !== null
    );
    return new Set(assignedUserIds).size !== assignedUserIds.length;
}

interface UseControlActivityReassignUtilsProps {
    selectedRows: Row<ControlActivityTableRow>[];
    deselectAll: () => void;
}

export function useControlActivityReassignUtils({
    selectedRows,
    deselectAll,
}: UseControlActivityReassignUtilsProps) {
    const { t } = useTranslation();
    const [reassignModalOpen, setReassignModalOpen] = useState(false);
    const [isReassigning, setIsReassigning] = useState(false);

    const controlResultIds = selectedRows.map((row) => row.original.activityId);
    const hasArchivedSelection = selectedRows.some(
        (row) => row.original.archived
    );
    const hasPermission = selectedRows.every(
        (row) => row.original.canEditProgram
    );

    // Reviewer count must be uniform across the selection and every reassigned step index has to exist on every activity
    const reviewerCounts = selectedRows.map(
        (row) => row.original.reviewerCount
    );
    const reviewerCount = reviewerCounts[0] ?? 0;
    const hasMixedReviewerCount = reviewerCounts.some(
        (count) => count !== reviewerCount
    );

    const reassignToolState = ((): TextToolState => {
        if (selectedRows.length === 0) {
            return {
                type: "DISABLED",
                reason: t("ControlActivitiesList_Tooltip_NoSelection"),
            };
        }
        if (hasArchivedSelection) {
            return {
                type: "DISABLED",
                reason: t("ControlActivitiesList_Reassign_Tooltip_Conflict"),
            };
        }
        if (!hasPermission) {
            return {
                type: "DISABLED",
                reason: t(
                    "ControlActivitiesList_Tooltip_Reassign_NoPermission"
                ),
            };
        }
        if (hasMixedReviewerCount) {
            // Reassigning a step that doesn't exist on every activity would fail
            // server-side, so a mixed reviewer setup can't be reassigned in bulk.
            return {
                type: "DISABLED",
                reason: t("ControlResult_Reassign_HeterogeneousSelection"),
            };
        }
        return { type: "ENABLED" };
    })();

    const openReassignModal = () => setReassignModalOpen(true);
    const closeReassignModal = () => setReassignModalOpen(false);

    const handleReassign = async (selection: ReassignSelection) => {
        const breaksSegregationOfDuties = selectedRows.some((row) =>
            wouldBreakSegregationOfDuties(row.original, selection)
        );
        if (breaksSegregationOfDuties) {
            emitNotification(
                "error",
                t("ControlResult_Error_ReassignWillBreakSegregationOfDuties")
            );
            return;
        }

        const picks = REASSIGN_ROLES.flatMap((role) => {
            const assignee = selection[role.key];
            return assignee ? [{ role, assignee }] : [];
        });

        setIsReassigning(true);
        try {
            const newAssignees: ReassignIds["newAssignees"] = {};
            picks.forEach(({ role, assignee }) => {
                newAssignees[role.stepIndex] = toNewAssignee(assignee);
            });
            await postControlResultsReassign({
                controlResultIds,
                newAssignees,
            });
            // Assignee names shown in the list changed, so refetch.
            await queryClient.invalidateQueries({
                queryKey: getGetControlResultsInfiniteQueryKey(),
            });

            // One notification per reassigned role (mirrors the legacy behaviour).
            picks.forEach(({ role, assignee }) => {
                emitNotification(
                    "success",
                    t("ControlActivitiesList_Reassign_Success", {
                        count: controlResultIds.length,
                        assignee: assigneeName(assignee),
                        role: t(role.labelKey),
                    })
                );
            });

            deselectAll();
            closeReassignModal();
        } catch {
            picks.forEach(({ role, assignee }) => {
                emitNotification(
                    "error",
                    t("ControlActivitiesList_Reassign_Failed", {
                        assignee: assigneeName(assignee),
                        role: t(role.labelKey),
                    })
                );
            });
        } finally {
            setIsReassigning(false);
        }
    };

    return {
        reassignToolState,
        reassignModalOpen,
        openReassignModal,
        closeReassignModal,
        handleReassign,
        isReassigning,
        reviewerCount,
    };
}
